import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
	Box,
	Paper,
	Typography,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogContentText,
	DialogActions,
	TextField,
	IconButton,
	Button,
	Alert,
	Switch,
	FormControlLabel,
	MenuItem,
	Select,
	FormControl,
	InputLabel,
	Chip,
	ListItemText,
	Checkbox,
	OutlinedInput,
	Autocomplete,
} from "@mui/material";
import {
	Edit as EditIcon,
	Plus as PlusIcon,
	Trash2 as TrashIcon,
	FileText as ContentIcon,
	Search as SearchIcon,
	AlertTriangle as WarningIcon,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";

// Modified fields config to change grade field type
const FIELDS_CONFIG = [
	{ name: "name", label: "Name", type: "text", required: true },
	{ name: "description", label: "Description", type: "text", multiline: true },
	{
		name: "concept",
		label: "Concept",
		type: "text",
		required: true,
		multiline: true,
	},
	{ name: "playorder", label: "Play Order", type: "number", required: true },
	{ name: "status", label: "Status", type: "boolean" },
	{ name: "thumbnail", label: "Thumbnail", type: "file" },
	{
		name: "selectedGrades",
		label: "Grades",
		type: "multiselect_searchable",
		required: true,
	},
];

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
	PaperProps: {
		style: {
			maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
			width: 250,
		},
	},
};

const ManageCategories = () => {
	const navigate = useNavigate();
	const { supabase } = useAuth();
	const [categories, setCategories] = useState([]);
	const [grades, setGrades] = useState([]);
	const [openDialog, setOpenDialog] = useState(false);
	const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
	const [categoryToDelete, setCategoryToDelete] = useState(null);
	const [selectedItem, setSelectedItem] = useState(null);
	const [editForm, setEditForm] = useState({
		selectedGrades: [],
	});
	const [successMessage, setSuccessMessage] = useState("");
	const [errorMessage, setErrorMessage] = useState("");
	const [thumbnailFile, setThumbnailFile] = useState(null);
	const [formErrors, setFormErrors] = useState({});
	const [searchQuery, setSearchQuery] = useState("");
	const [selectedGradeFilter, setSelectedGradeFilter] = useState("all");
	const [selectedTopicFilter, setSelectedTopicFilter] = useState("all");
	const [filteredCategories, setFilteredCategories] = useState([]);
	const [topics, setTopics] = useState([]);
	const [addTopicDialogOpen, setAddTopicDialogOpen] = useState(false);
	const [newTopicName, setNewTopicName] = useState("");
	const [savingTopic, setSavingTopic] = useState(false);

	const fetchTopics = async () => {
		try {
			const { data, error } = await supabase
				.from("funmath_topics")
				.select("id, name, sort_order")
				.eq("status", true)
				.order("sort_order", { ascending: true })
				.order("name", { ascending: true });

			if (error) throw error;
			setTopics(data || []);
		} catch (err) {
			console.error("Error fetching topics:", err);
		}
	};

	const fetchGrades = async () => {
		try {
			const { data, error } = await supabase
				.from("grades")
				.select("*")
				.order("name");

			if (error) throw error;
			setGrades(data || []);
		} catch (err) {
			console.error("Error fetching grades:", err);
			setErrorMessage("Error loading grades");
		}
	};

	const fetchCategories = async () => {
		try {
			// Query to get all categories with their grades
			const { data, error } = await supabase
				.from("course_categories")
				.select(
					`
                    *,
                    funmath_topics ( id, name ),
                    category_grades(
                        id,
                        grade_id,
                        grades:grade_id(
                            id,
                            name
                        )
                    )
                `
				)
				.order("playorder");

			if (error) throw error;

			// Process the data to make it easier to work with
			const processedData = data.map((category) => {
				// Extract grade data from the nested structure
				const categoryGrades = category.category_grades.map((cg) => ({
					id: cg.grade_id,
					name: cg.grades.name,
				}));

				return {
					...category,
					grades: categoryGrades,
					topic_id: category.topic_id ?? null,
				};
			});

			setCategories(processedData || []);
		} catch (err) {
			console.error("Error fetching categories:", err);
			setErrorMessage("Error loading categories");
		}
	};

	useEffect(() => {
		fetchGrades();
		fetchTopics();
		fetchCategories();
	}, []);

	useEffect(() => {
		let filtered = [...categories];

		// Apply search filter
		if (searchQuery) {
			filtered = filtered.filter(
				(category) =>
					category.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
					category.concept.toLowerCase().includes(searchQuery.toLowerCase())
			);
		}

		// Apply grade filter
		if (selectedGradeFilter !== "all") {
			filtered = filtered.filter((category) =>
				// Check if any of the category's grades match the filter
				category.grades.some((grade) => grade.id === selectedGradeFilter)
			);
		}

		if (selectedTopicFilter !== "all") {
			if (selectedTopicFilter === "none") {
				filtered = filtered.filter((c) => !c.topic_id);
			} else {
				filtered = filtered.filter((c) => c.topic_id === selectedTopicFilter);
			}
		}

		setFilteredCategories(filtered);
	}, [categories, searchQuery, selectedGradeFilter, selectedTopicFilter]);

	const handleSearchChange = (event) => {
		setSearchQuery(event.target.value);
	};

	const handleGradeFilterChange = (event) => {
		setSelectedGradeFilter(event.target.value);
	};

	const handleTopicFilterChange = (event) => {
		setSelectedTopicFilter(event.target.value);
	};

	const handleSaveNewTopic = async () => {
		const name = newTopicName.trim();
		if (!name) {
			setErrorMessage("Topic name is required");
			return;
		}
		setSavingTopic(true);
		setErrorMessage("");
		try {
			const nextOrder =
				topics.length > 0
					? Math.max(...topics.map((t) => t.sort_order ?? 0)) + 10
					: 10;
			const { data, error } = await supabase
				.from("funmath_topics")
				.insert([
					{
						name,
						sort_order: nextOrder,
						status: true,
						updated_at: new Date().toISOString(),
					},
				])
				.select("id, name")
				.single();

			if (error) throw error;
			await fetchTopics();
			setEditForm((prev) => ({ ...prev, topic_id: data.id }));
			setAddTopicDialogOpen(false);
			setNewTopicName("");
			setSuccessMessage(`Topic "${data.name}" added`);
			setTimeout(() => setSuccessMessage(""), 3000);
		} catch (err) {
			console.error(err);
			setErrorMessage(err.message || "Could not add topic");
		} finally {
			setSavingTopic(false);
		}
	};

	const validateForm = () => {
		const errors = {};
		FIELDS_CONFIG.forEach((field) => {
			if (field.required) {
				if (
					field.name === "selectedGrades" &&
					(!editForm.selectedGrades || editForm.selectedGrades.length === 0)
				) {
					errors[field.name] = `${field.label} is required`;
				} else if (field.name !== "selectedGrades" && !editForm[field.name]) {
					errors[field.name] = `${field.label} is required`;
				}
			}
		});
		setFormErrors(errors);
		return Object.keys(errors).length === 0;
	};

	const handleAddNew = () => {
		setSelectedItem(null);
		setEditForm({
			status: true,
			playorder: categories.length + 1,
			selectedGrades: [],
			topic_id: null,
		});
		setThumbnailFile(null);
		setFormErrors({});
		setOpenDialog(true);
	};

	const handleEdit = (item) => {
		// For editing, we need to pull the grade IDs from the category's grades array
		const selectedGrades = item.grades.map((grade) => grade.id);

		setSelectedItem(item);
		setEditForm({
			...item,
			selectedGrades: selectedGrades,
			topic_id: item.topic_id ?? null,
		});
		setThumbnailFile(null);
		setFormErrors({});
		setOpenDialog(true);
	};

	const handleDeleteConfirmation = (category) => {
		setCategoryToDelete(category);
		setOpenDeleteDialog(true);
	};

	const handleDelete = async () => {
		if (!categoryToDelete) return;

		try {
			// First check if there are any contents associated with this category
			const { data: contents, error: contentsError } = await supabase
				.from("course_contents")
				.select("id")
				.eq("category_id", categoryToDelete.id);

			if (contentsError) throw contentsError;

			if (contents && contents.length > 0) {
				setErrorMessage(
					"Cannot delete category that has contents. Please delete the contents first."
				);
				setOpenDeleteDialog(false);
				return;
			}

			// Delete the category
			const { error } = await supabase
				.from("course_categories")
				.delete()
				.eq("id", categoryToDelete.id);

			if (error) throw error;

			setSuccessMessage("Category deleted successfully");
			fetchCategories();

			// Clear success message after 3 seconds
			setTimeout(() => setSuccessMessage(""), 3000);
		} catch (err) {
			console.error("Error deleting category:", err);
			setErrorMessage("Error deleting category");
		} finally {
			setOpenDeleteDialog(false);
			setCategoryToDelete(null);
		}
	};

	const handleFileChange = (event) => {
		const file = event.target.files[0];
		if (file) {
			if (file.size > 5 * 1024 * 1024) {
				// 5MB limit
				setErrorMessage("File size should not exceed 5MB");
				return;
			}
			setThumbnailFile(file);
		}
	};

	const uploadThumbnail = async (file) => {
		const fileExt = file.name.split(".").pop();
		const fileName = `${Date.now()}-${Math.random()
			.toString(36)
			.substring(7)}.${fileExt}`;
		const filePath = `course-categories/${fileName}`;

		const { error: uploadError } = await supabase.storage
			.from("thumbnails")
			.upload(filePath, file);

		if (uploadError) throw uploadError;

		const {
			data: { publicUrl },
		} = supabase.storage.from("thumbnails").getPublicUrl(filePath);

		return publicUrl;
	};

	const handleSave = async () => {
		try {
			if (!validateForm()) {
				return;
			}

			const now = new Date().toISOString();

			// Prepare the data to save (excluding the selectedGrades field)
			const {
				selectedGrades,
				grades,
				category_grades,
				funmath_topics,
				...dataToSave
			} = editForm;

			if (dataToSave.topic_id === "" || dataToSave.topic_id === undefined) {
				dataToSave.topic_id = null;
			}

			// Add timestamps
			dataToSave.updated_at = now;

			// Add thumbnail if uploaded
			if (thumbnailFile) {
				const thumbnailUrl = await uploadThumbnail(thumbnailFile);
				dataToSave.thumbnail = thumbnailUrl;
			}

			let categoryId;

			if (!selectedItem) {
				// Insert new category
				const {
					data: { user },
				} = await supabase.auth.getUser();

				dataToSave.created_at = now;
				dataToSave.created_by = user?.id;

				const { data, error } = await supabase
					.from("course_categories")
					.insert([dataToSave])
					.select();

				if (error) throw error;
				categoryId = data[0].id;
				setSuccessMessage("New category added successfully");
			} else {
				// Update existing category
				const { error } = await supabase
					.from("course_categories")
					.update(dataToSave)
					.eq("id", selectedItem.id);

				if (error) throw error;
				categoryId = selectedItem.id;
				setSuccessMessage("Category updated successfully");
			}

			// Now handle the grade associations
			if (categoryId) {
				// First, delete existing associations
				const { error: deleteError } = await supabase
					.from("category_grades")
					.delete()
					.eq("category_id", categoryId);

				if (deleteError) throw deleteError;

				// Then insert new associations
				if (selectedGrades && selectedGrades.length > 0) {
					const gradeAssociations = selectedGrades.map((gradeId) => ({
						category_id: categoryId,
						grade_id: gradeId,
					}));

					const { error: insertError } = await supabase
						.from("category_grades")
						.insert(gradeAssociations);

					if (insertError) throw insertError;
				}
			}

			setOpenDialog(false);
			fetchCategories();

			setTimeout(() => setSuccessMessage(""), 3000);
		} catch (err) {
			console.error("Error saving category:", err);
			setErrorMessage("Error saving category: " + err.message);
		}
	};

	const handleManageContents = (categoryId) => {
		navigate(`/manage-course-contents/${categoryId}`);
	};

	// Helper function to display grades as a comma-separated list
	const formatGradesList = (grades) => {
		if (!grades || grades.length === 0) return "None";

		return grades.map((g) => g.name).join(", ");
	};

	return (
		<Box sx={{ p: 3 }}>
			{successMessage && (
				<Alert severity="success" sx={{ mb: 2 }}>
					{successMessage}
				</Alert>
			)}
			{errorMessage && (
				<Alert severity="error" sx={{ mb: 2 }}>
					{errorMessage}
				</Alert>
			)}

			<Box
				sx={{
					mb: 3,
					display: "flex",
					gap: 2,
					alignItems: "center",
				}}>
				<Typography variant="h5" sx={{ mr: 2 }}>
					Game Categories
				</Typography>
				<TextField
					placeholder="Search categories..."
					value={searchQuery}
					onChange={handleSearchChange}
					size="small"
					sx={{ flex: 1 }}
					InputProps={{
						startAdornment: <SearchIcon size={20} style={{ marginRight: 8 }} />,
					}}
				/>
				<FormControl sx={{ minWidth: 200 }} size="small">
					<InputLabel id="grade-filter-label">Filter by Grade</InputLabel>
					<Select
						labelId="grade-filter-label"
						value={selectedGradeFilter}
						label="Filter by Grade"
						onChange={handleGradeFilterChange}>
						<MenuItem value="all">All Grades</MenuItem>
						{grades.map((grade) => (
							<MenuItem key={grade.id} value={grade.id}>
								{grade.name}
							</MenuItem>
						))}
					</Select>
				</FormControl>
				<FormControl sx={{ minWidth: 200 }} size="small">
					<InputLabel id="topic-filter-label">Filter by Topic</InputLabel>
					<Select
						labelId="topic-filter-label"
						value={selectedTopicFilter}
						label="Filter by Topic"
						onChange={handleTopicFilterChange}>
						<MenuItem value="all">All Topics</MenuItem>
						<MenuItem value="none">No topic</MenuItem>
						{topics.map((t) => (
							<MenuItem key={t.id} value={t.id}>
								{t.name}
							</MenuItem>
						))}
					</Select>
				</FormControl>
				<Button
					variant="contained"
					startIcon={<PlusIcon size={20} />}
					onClick={handleAddNew}>
					Add Category
				</Button>
			</Box>

			<Paper
				elevation={0}
				sx={{
					p: 3,
					border: "1px solid",
					borderColor: "divider",
					borderRadius: 2,
				}}>
				<TableContainer>
					<Table>
						<TableHead>
							<TableRow>
								<TableCell>Name</TableCell>
								<TableCell>Concept</TableCell>
								<TableCell>Topic</TableCell>
								<TableCell>Grades</TableCell>
								<TableCell>Play Order</TableCell>
								<TableCell>Status</TableCell>
								<TableCell align="right">Actions</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{filteredCategories.map((category) => (
								<TableRow key={category.id}>
									<TableCell>{category.name}</TableCell>
									<TableCell>{category.concept}</TableCell>
									<TableCell>
										{category.funmath_topics?.name ? (
											<Chip
												label={category.funmath_topics.name}
												size="small"
												sx={{ bgcolor: "#e8f5e9", color: "#2e7d32" }}
											/>
										) : (
											<Typography variant="body2" color="text.secondary">
												—
											</Typography>
										)}
									</TableCell>
									<TableCell>
										{category.grades && category.grades.length > 0 ? (
											<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
												{category.grades.map((grade) => (
													<Chip
														key={grade.id}
														label={grade.name}
														size="small"
														sx={{ background: "#f0f4ff", color: "#4338ca" }}
													/>
												))}
											</Box>
										) : (
											"None"
										)}
									</TableCell>
									<TableCell>{category.playorder}</TableCell>
									<TableCell>
										<Switch checked={category.status} size="small" disabled />
									</TableCell>
									<TableCell align="right">
										<IconButton
											size="small"
											onClick={() => handleManageContents(category.id)}
											sx={{ mr: 1 }}
											title="Manage Contents">
											<ContentIcon size={16} />
										</IconButton>
										<IconButton
											size="small"
											onClick={() => handleEdit(category)}
											sx={{ mr: 1 }}>
											<EditIcon size={16} />
										</IconButton>
										<IconButton
											size="small"
											color="error"
											onClick={() => handleDeleteConfirmation(category)}>
											<TrashIcon size={16} />
										</IconButton>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</TableContainer>

				{filteredCategories.length === 0 && (
					<Box sx={{ py: 4, textAlign: "center" }}>
						<Typography color="text.secondary">No categories found</Typography>
					</Box>
				)}
			</Paper>

			{/* Edit/Add Dialog */}
			<Dialog
				open={openDialog}
				onClose={() => setOpenDialog(false)}
				maxWidth="sm"
				fullWidth>
				<DialogTitle>
					{selectedItem ? "Edit Category" : "Add New Category"}
				</DialogTitle>
				<DialogContent>
					<FormControl fullWidth sx={{ mt: 1 }}>
						<InputLabel id="game-topic-label">Topic</InputLabel>
						<Select
							labelId="game-topic-label"
							label="Topic"
							value={editForm.topic_id || ""}
							onChange={(e) => {
								const v = e.target.value;
								if (v === "__add_new__") {
									setAddTopicDialogOpen(true);
								} else {
									setEditForm({
										...editForm,
										topic_id: v ? v : null,
									});
								}
							}}>
							<MenuItem value="">
								<em>None</em>
							</MenuItem>
							{topics.map((t) => (
								<MenuItem key={t.id} value={t.id}>
									{t.name}
								</MenuItem>
							))}
							<MenuItem value="__add_new__" sx={{ fontWeight: 700 }}>
								+ Add new topic…
							</MenuItem>
						</Select>
					</FormControl>
					{FIELDS_CONFIG.map((field) => {
						if (field.type === "boolean") {
							return (
								<FormControlLabel
									key={field.name}
									control={
										<Switch
											checked={editForm[field.name] || false}
											onChange={(e) =>
												setEditForm({
													...editForm,
													[field.name]: e.target.checked,
												})
											}
										/>
									}
									label={field.label}
									sx={{ mt: 2, display: "block" }}
								/>
							);
						}

						if (field.type === "file") {
							return (
								<Box key={field.name} sx={{ mt: 2 }}>
									<input
										accept="image/*"
										type="file"
										onChange={handleFileChange}
										style={{ display: "none" }}
										id="thumbnail-file"
									/>
									<label htmlFor="thumbnail-file">
										<Button variant="outlined" component="span">
											Upload Thumbnail
										</Button>
									</label>
									{(thumbnailFile || editForm.thumbnail) && (
										<Typography variant="caption" sx={{ ml: 2 }}>
											{thumbnailFile
												? thumbnailFile.name
												: "Current thumbnail exists"}
										</Typography>
									)}
								</Box>
							);
						}

						if (field.type === "multiselect_searchable") {
							const selectedGradeObjects = grades.filter((grade) =>
								(editForm[field.name] || []).includes(grade.id)
							);

							return (
								<Box key={field.name} sx={{ mt: 2 }}>
									<Autocomplete
										multiple
										options={grades}
										getOptionLabel={(grade) => grade.name || ""}
										value={selectedGradeObjects}
										onChange={(event, newValue) => {
											const selectedIds = newValue.map((grade) => grade.id);
											setEditForm({
												...editForm,
												[field.name]: selectedIds,
											});
										}}
										renderInput={(params) => (
											<TextField
												{...params}
												label={field.label}
												fullWidth
												placeholder="Search and select grades..."
												error={!!formErrors[field.name]}
												helperText={formErrors[field.name]}
												required={field.required}
												InputProps={{
													...params.InputProps,
													startAdornment: (
														<>
															<SearchIcon
																size={16}
																style={{ marginRight: 4, color: "#666" }}
															/>
															{params.InputProps.startAdornment}
														</>
													),
												}}
											/>
										)}
										renderOption={(props, grade) => (
											<Box
												component="li"
												{...props}
												sx={{
													"&:hover": { bgcolor: "action.hover" },
													py: 1,
												}}>
												<Checkbox
													checked={(editForm[field.name] || []).includes(
														grade.id
													)}
													sx={{ mr: 1 }}
												/>
												<Typography variant="body2">{grade.name}</Typography>
											</Box>
										)}
										renderTags={(value, getTagProps) =>
											value.map((grade, index) => (
												<Chip
													{...getTagProps({ index })}
													key={grade.id}
													label={grade.name}
													size="small"
													color="primary"
													variant="outlined"
												/>
											))
										}
										filterOptions={(options, { inputValue }) => {
											if (!inputValue) return options;
											return options.filter((option) => {
												const name = (option.name || "").toLowerCase();
												const search = inputValue.toLowerCase();
												return name.includes(search);
											});
										}}
										isOptionEqualToValue={(option, value) =>
											option.id === value.id
										}
										disableCloseOnSelect
										openOnFocus
										selectOnFocus
										clearOnBlur
										handleHomeEndKeys
										noOptionsText="No grades found"
										loadingText="Loading grades..."
										componentsProps={{
											popper: {
												modifiers: [
													{
														name: "offset",
														options: {
															offset: [0, 4],
														},
													},
												],
											},
										}}
									/>
								</Box>
							);
						}

						if (field.type === "multiselect") {
							return (
								<FormControl
									key={field.name}
									fullWidth
									sx={{ mt: 2 }}
									error={!!formErrors[field.name]}>
									<InputLabel id={`${field.name}-label`}>
										{field.label}
									</InputLabel>
									<Select
										labelId={`${field.name}-label`}
										multiple
										value={editForm[field.name] || []}
										onChange={(e) =>
											setEditForm({
												...editForm,
												[field.name]: e.target.value,
											})
										}
										input={<OutlinedInput label={field.label} />}
										renderValue={(selected) => (
											<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
												{selected.map((value) => {
													const grade = grades.find((g) => g.id === value);
													return (
														<Chip
															key={value}
															label={grade ? grade.name : value}
															size="small"
														/>
													);
												})}
											</Box>
										)}
										MenuProps={MenuProps}>
										{grades.map((grade) => (
											<MenuItem key={grade.id} value={grade.id}>
												<Checkbox
													checked={
														(editForm[field.name] || []).indexOf(grade.id) > -1
													}
												/>
												<ListItemText primary={grade.name} />
											</MenuItem>
										))}
									</Select>
									{formErrors[field.name] && (
										<Typography variant="caption" color="error">
											{formErrors[field.name]}
										</Typography>
									)}
								</FormControl>
							);
						}

						return (
							<TextField
								key={field.name}
								label={field.label}
								type={field.type}
								fullWidth
								multiline={field.multiline}
								rows={field.multiline ? 4 : 1}
								value={editForm[field.name] || ""}
								onChange={(e) =>
									setEditForm({
										...editForm,
										[field.name]: e.target.value,
									})
								}
								error={!!formErrors[field.name]}
								helperText={formErrors[field.name]}
								required={field.required}
								sx={{ mt: 2 }}
							/>
						);
					})}
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setOpenDialog(false)}>Cancel</Button>
					<Button onClick={handleSave} variant="contained">
						{selectedItem ? "Update" : "Add"}
					</Button>
				</DialogActions>
			</Dialog>

			<Dialog
				open={addTopicDialogOpen}
				onClose={() => !savingTopic && setAddTopicDialogOpen(false)}
				maxWidth="xs"
				fullWidth>
				<DialogTitle>Add topic</DialogTitle>
				<DialogContent>
					<TextField
						autoFocus
						margin="dense"
						label="Topic name"
						fullWidth
						value={newTopicName}
						onChange={(e) => setNewTopicName(e.target.value)}
						onKeyDown={(e) => {
							if (e.key === "Enter") handleSaveNewTopic();
						}}
					/>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setAddTopicDialogOpen(false)} disabled={savingTopic}>
						Cancel
					</Button>
					<Button
						onClick={handleSaveNewTopic}
						variant="contained"
						disabled={savingTopic || !newTopicName.trim()}>
						Save topic
					</Button>
				</DialogActions>
			</Dialog>

			{/* Delete Confirmation Dialog */}
			<Dialog
				open={openDeleteDialog}
				onClose={() => setOpenDeleteDialog(false)}
				aria-labelledby="delete-dialog-title"
				aria-describedby="delete-dialog-description">
				<DialogTitle
					id="delete-dialog-title"
					sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					<WarningIcon size={20} color="#ed6c02" />
					Confirm Deletion
				</DialogTitle>
				<DialogContent>
					<DialogContentText id="delete-dialog-description">
						Are you sure you want to delete the category "
						<strong>{categoryToDelete?.name}</strong>"? This action cannot be
						undone.
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setOpenDeleteDialog(false)} color="primary">
						Cancel
					</Button>
					<Button onClick={handleDelete} color="error" variant="contained">
						Delete
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
};

export default ManageCategories;
